/**
 * Resident Approvals Pinia store — manages the pending/approved/denied approval
 * queues and the audit log tab for the ResidentApprovalsView.
 *
 * Key invariant: `approve()` removes an item from `pending` optimistically
 * ONLY when the result is "Approved". AwaitingSecondApprover and AlreadyApproved
 * keep the item in pending (the DualApprovalBanner updates in-place).
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/api/client';
export const useResidentsStore = defineStore('residents', () => {
    // ── State ─────────────────────────────────────────────────────────────────
    const pending = ref([]);
    const approved = ref([]);
    const denied = ref([]);
    const auditLog = ref([]);
    const selectedIds = ref(new Set());
    const isLoading = ref(false);
    const error = ref(null);
    // ── Computed ──────────────────────────────────────────────────────────────
    /**
     * True only when there is at least one selected item AND all selected items
     * are non-flagged (requiresDualApproval === false). Flagged cards must always
     * be reviewed individually.
     */
    const canBulkApprove = computed(() => {
        if (selectedIds.value.size === 0)
            return false;
        for (const id of selectedIds.value) {
            const item = pending.value.find((p) => p.id === id);
            if (!item || item.requiresDualApproval)
                return false;
        }
        return true;
    });
    const pendingCount = computed(() => pending.value.length);
    const approvedCount = computed(() => approved.value.length);
    const deniedCount = computed(() => denied.value.length);
    const flaggedCount = computed(() => pending.value.filter((p) => p.requiresDualApproval).length);
    // ── Actions ───────────────────────────────────────────────────────────────
    async function fetchPending() {
        isLoading.value = true;
        error.value = null;
        try {
            const { data, error: apiError } = await api.GET('/v1/board/residents/pending', {
                params: { query: { status: 'pending' } },
            });
            if (apiError)
                throw new Error(JSON.stringify(apiError));
            pending.value = data ?? [];
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load pending approvals.';
        }
        finally {
            isLoading.value = false;
        }
    }
    async function fetchApproved() {
        isLoading.value = true;
        error.value = null;
        try {
            const { data, error: apiError } = await api.GET('/v1/board/residents/pending', {
                params: { query: { status: 'approved' } },
            });
            if (apiError)
                throw new Error(JSON.stringify(apiError));
            approved.value = data ?? [];
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load approved residents.';
        }
        finally {
            isLoading.value = false;
        }
    }
    async function fetchDenied() {
        isLoading.value = true;
        error.value = null;
        try {
            const { data, error: apiError } = await api.GET('/v1/board/residents/pending', {
                params: { query: { status: 'denied' } },
            });
            if (apiError)
                throw new Error(JSON.stringify(apiError));
            denied.value = data ?? [];
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load denied residents.';
        }
        finally {
            isLoading.value = false;
        }
    }
    async function fetchAuditLog() {
        isLoading.value = true;
        error.value = null;
        try {
            const { data, error: apiError } = await api.GET('/v1/board/residents/audit-log', {
                params: { query: { limit: 50 } },
            });
            if (apiError)
                throw new Error(JSON.stringify(apiError));
            auditLog.value = data ?? [];
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load audit log.';
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Approve a resident. Optimistically removes from `pending` ONLY when the
     * result is "Approved". Otherwise updates the item in-place (dual-approval flow).
     */
    async function approve(id) {
        const { data, error: apiError } = await api.POST('/v1/board/residents/pending/{id}/approve', {
            params: { path: { id } },
        });
        if (apiError)
            throw new Error(JSON.stringify(apiError));
        const result = (data?.result ?? 'Approved');
        if (result === 'Approved') {
            pending.value = pending.value.filter((p) => p.id !== id);
            selectedIds.value.delete(id);
        }
        else if (result === 'AwaitingSecondApprover') {
            // Re-fetch the item to get updated signedOffBy list
            try {
                const { data: detail } = await api.GET('/v1/board/residents/pending/{id}', {
                    params: { path: { id } },
                });
                if (detail) {
                    const idx = pending.value.findIndex((p) => p.id === id);
                    if (idx !== -1)
                        pending.value[idx] = detail;
                }
            }
            catch {
                // Non-fatal — the banner will still show on next full refresh
            }
        }
        return result;
    }
    /**
     * Deny a resident and move them to the denied list.
     */
    async function deny(id, reason) {
        const body = { reason };
        const { error: apiError } = await api.POST('/v1/board/residents/pending/{id}/deny', {
            params: { path: { id } },
            body,
        });
        if (apiError)
            throw new Error(JSON.stringify(apiError));
        const item = pending.value.find((p) => p.id === id);
        pending.value = pending.value.filter((p) => p.id !== id);
        selectedIds.value.delete(id);
        if (item) {
            denied.value.unshift({ ...item, status: 'Denied' });
        }
    }
    /**
     * Request more proof from a resident (sends email stub).
     */
    async function requestMoreProof(id, message) {
        const body = { message };
        const { error: apiError } = await api.POST('/v1/board/residents/pending/{id}/request-more-proof', {
            params: { path: { id } },
            body,
        });
        if (apiError)
            throw new Error(JSON.stringify(apiError));
    }
    function toggleSelect(id) {
        if (selectedIds.value.has(id)) {
            selectedIds.value.delete(id);
        }
        else {
            selectedIds.value.add(id);
        }
        // Force reactivity — Set mutations don't trigger Vue reactivity
        selectedIds.value = new Set(selectedIds.value);
    }
    function clearSelection() {
        selectedIds.value = new Set();
    }
    function $reset() {
        pending.value = [];
        approved.value = [];
        denied.value = [];
        auditLog.value = [];
        selectedIds.value = new Set();
        isLoading.value = false;
        error.value = null;
    }
    return {
        pending,
        approved,
        denied,
        auditLog,
        selectedIds,
        isLoading,
        error,
        canBulkApprove,
        pendingCount,
        approvedCount,
        deniedCount,
        flaggedCount,
        fetchPending,
        fetchApproved,
        fetchDenied,
        fetchAuditLog,
        approve,
        deny,
        requestMoreProof,
        toggleSelect,
        clearSelection,
        $reset,
    };
});
